import { useMemo, useState } from 'react'
import { Network, Recycle, X } from 'lucide-react'
import { useAppStore } from '@core/store'
import { isFeatureEnabled } from '@core/features'
import {
  assessCircularity,
  buildModelGraph,
  queryModelGraph,
  type ModelGraph,
} from './modelGraph'

/** Quantos resultados a lista mostra de uma vez. */
const RESULT_LIMIT = 50

export default function ModelIntelligencePanel() {
  const open = useAppStore((state) => state.modelIntelligenceOpen)
  const setOpen = useAppStore((state) => state.setModelIntelligenceOpen)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [graph, setGraph] = useState<ModelGraph | null>(null)
  const [tab, setTab] = useState<'graph' | 'circularity'>(() =>
    !isFeatureEnabled('graphRag') && isFeatureEnabled('circularity')
      ? 'circularity'
      : 'graph',
  )
  const activeModelId = useAppStore((state) => state.activeModelId)
  const activeGraph = graph?.modelId === activeModelId ? graph : null
  const results = useMemo(
    () => activeGraph ? queryModelGraph(activeGraph, query, RESULT_LIMIT) : [],
    [activeGraph, query],
  )
  const assessment = useMemo(
    () => activeGraph ? assessCircularity(activeGraph.nodes) : null,
    [activeGraph],
  )
  if (!open || (!isFeatureEnabled('graphRag') && !isFeatureEnabled('circularity'))) return null

  const build = async () => {
    if (!activeModelId) return
    setError(null)
    setBusy(true)
    try {
      setGraph(await buildModelGraph(activeModelId))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const download = (value: unknown, filename: string) => {
    const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    anchor.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  return (
    <aside className="pointer-events-auto absolute left-3 top-14 z-30 flex max-h-[78%] w-[430px] flex-col overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-panel)] text-xs shadow-2xl">
      <header className="flex items-center gap-2 border-b border-[var(--color-border)] px-3 py-2">
        <Network size={15} className="text-[var(--color-accent)]" />
        <strong>Inteligência do modelo</strong>
        <button type="button" className="ml-auto p-1" onClick={() => setOpen(false)} title="Fechar"><X size={14} /></button>
      </header>
      <div className="flex border-b border-[var(--color-border)]">
        {isFeatureEnabled('graphRag') && <button type="button" className={`flex-1 px-3 py-2 ${tab === 'graph' ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]' : ''}`} onClick={() => setTab('graph')}>Grafo local</button>}
        {isFeatureEnabled('circularity') && <button type="button" className={`flex flex-1 items-center justify-center gap-1 px-3 py-2 ${tab === 'circularity' ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]' : ''}`} onClick={() => setTab('circularity')}><Recycle size={13} /> Circularidade</button>}
      </div>
      {!activeGraph ? (
        <div className="p-4">
          <p className="mb-3 text-[var(--color-text-muted)]">Constrói no browser um índice de entidades, propriedades e relações espaciais. Não usa um serviço externo.</p>
          <button type="button" disabled={!activeModelId || busy} className="w-full rounded bg-[var(--color-accent)] px-3 py-2 font-medium text-white disabled:opacity-50" onClick={() => void build()}>{busy ? 'A construir…' : 'Construir grafo do modelo ativo'}</button>
          {error && <p className="mt-3 rounded border border-[var(--color-error-border)] bg-[var(--color-error-bg)] p-2 text-[var(--color-error-text)]">{error}</p>}
        </div>
      ) : tab === 'graph' ? (
        <div className="flex min-h-0 flex-1 flex-col p-3">
          <div className="mb-2 flex items-center gap-2">
            <span>{activeGraph.nodes.length.toLocaleString('pt-PT')} nós · {activeGraph.edges.toLocaleString('pt-PT')} relações</span>
            <button type="button" className="ml-auto underline" onClick={() => download(activeGraph, 'vn2r-model-graph.json')}>Exportar JSON</button>
          </div>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Procurar classe, nome, propriedade ou GlobalId" className="mb-2 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5" />
          <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto">
            {results.map((node) => (
              <li key={node.id}>
                <button type="button" className="w-full rounded border border-[var(--color-border)] p-2 text-left hover:bg-[var(--color-surface)]" onClick={() => useAppStore.getState().applyBulkSelection([{ modelId: node.modelId, localId: node.localId }], 'replace')}>
                  <div className="font-medium">{node.name || '(sem nome)'}</div>
                  <div className="text-[10px] text-[var(--color-text-muted)]">{node.ifcClass} · #{node.localId} · {node.neighbours.length} relações</div>
                </button>
              </li>
            ))}
            {results.length >= RESULT_LIMIT && (
              // Um limite calado lê-se como "é só isto". Se corta, diz.
              <li className="px-2 py-1 text-[10px] text-[var(--color-text-muted)]">
                Mostra os primeiros {RESULT_LIMIT}. Restringe a procura para
                ver o resto.
              </li>
            )}
          </ul>
        </div>
      ) : assessment ? (
        <div className="space-y-3 p-4">
          <div className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] p-3 text-center">
            <div className="text-3xl font-semibold text-[var(--color-accent)]">{assessment.documentationScore}%</div>
            <div className="text-[var(--color-text-muted)]">índice de documentação para reutilização</div>
          </div>
          <p className="text-[var(--color-text-muted)]">Este valor conta campos preenchidos no modelo. Não declara que o edifício é circular nem sustentável.</p>
          {activeGraph.typeAndMaterialFrom !== 'ifc' && (
            <p className="rounded border border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] p-2 text-[var(--color-warn-text)]">
              Este modelo não traz o IFC por trás, e o tipo e o material só
              lá se leem. As duas linhas abaixo aparecem a zero porque não
              foram observadas, não porque o modelo não as tenha.
            </p>
          )}
          <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1">
            <dt>Elementos avaliados</dt><dd>{assessment.total}</dd>
            <dt title="Unidades, valores de propriedade e outras entidades sem GlobalId. Não são elementos por documentar, por isso ficam de fora da conta.">Fora da conta</dt><dd>{assessment.ignored}</dd>
            <dt>Com nome</dt><dd>{assessment.withName}</dd>
            <dt>Com dados de tipo</dt><dd>{assessment.withType}</dd>
            <dt>Com material documentado</dt><dd>{assessment.withMaterial}</dd>
            <dt>Com condição/reutilização</dt><dd>{assessment.withConditionOrReuseData}</dd>
          </dl>
          <button type="button" className="w-full rounded border border-[var(--color-border)] px-2 py-1.5" onClick={() => download(assessment, 'vn2r-circularity-assessment.json')}>Exportar avaliação JSON</button>
        </div>
      ) : null}
    </aside>
  )
}
